/* eslint-disable @typescript-eslint/ban-ts-comment */
// Globals
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';

// Modules
import {
  getFeedbacks,
  selectQuery,
  GetFeedbacksDTO,
  FeedbacksFiltersFields,
  changeQuery,
  getStatusTypeOptions,
  useManagersOptions,
} from '@/modules/Feedbacks';

// Models
import { FeedbackType } from '@/models/feedback.model';

// Components
import { RangePicker } from '@/components';
import { Input, Select } from '@/ui';
import { StyledFilterRow } from './PotentialPartnersFiltersContainerStyles';

type IPotentialPartnersFiltersContainer = {
  children?: ReactNode;
  className?: string;
};

const PotentialPartnersFiltersContainer = ({ children, className }: IPotentialPartnersFiltersContainer) => {
  const { t } = useTranslation(['feedbacks']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);
  const managersOptions = useManagersOptions();

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetFeedbacksDTO({ ...searchParams, type: FeedbackType.POTENTIAL_PARTNER });
        dispatch(getFeedbacks(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleDateChange = (_: any, dates: [string, string]) => {
    const newQuery = {
      ...query,
      [FeedbacksFiltersFields.DATE_FROM]: dates?.[0] || undefined,
      [FeedbacksFiltersFields.DATE_TO]: dates?.[1] || undefined,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleSelectFilter = (name: string) => (value: string) => {
    const newQuery = {
      ...query,
      [name]: value || undefined,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            placeholder={t('label_name')}
            name={FeedbacksFiltersFields.NAME}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.NAME]?.toString()}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('label_shop')}
            name={FeedbacksFiltersFields.TITLE}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.TITLE]?.toString()}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('label_phone')}
            name={FeedbacksFiltersFields.PHONE}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.PHONE]?.toString()}
            size="large"
            allowClear
          />
        </th>
        {/* <th>
          <Input
            placeholder={t('label_edrpoy')}
            name={FeedbacksFiltersFields.TAX_CODE}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.TAX_CODE]?.toString()}
            size="large"
            allowClear
          />
        </th> */}
        <th>
          <Input
            placeholder={t('label_comment')}
            name={FeedbacksFiltersFields.COMMENT}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.COMMENT]?.toString()}
            size="large"
            allowClear
          />
        </th>
        <th>
          <RangePicker
            placeholder={t('label_date')}
            name={FeedbacksFiltersFields.DATE_FROM}
            allowEmpty={[true, true]}
            onChange={handleDateChange}
            value={[
              // @ts-ignore
              query?.[FeedbacksFiltersFields.DATE_FROM] ? dayjs(query?.[FeedbacksFiltersFields.DATE_FROM]) : null,
              // @ts-ignore
              query?.[FeedbacksFiltersFields.DATE_TO] ? dayjs(query?.[FeedbacksFiltersFields.DATE_TO]) : null,
            ]}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            size="large"
            allowClear
            placeholder={t('label_manager')}
            value={query?.[FeedbacksFiltersFields.MANAGER_ID]}
            onChange={handleSelectFilter(FeedbacksFiltersFields.MANAGER_ID)}
            options={managersOptions}
          />
        </th>
        <th>
          <Select
            size="large"
            allowClear
            placeholder={t('label_status')}
            value={query?.[FeedbacksFiltersFields.STATUS]}
            onChange={handleSelectFilter(FeedbacksFiltersFields.STATUS)}
            options={getStatusTypeOptions(t)}
          />
        </th>
      </StyledFilterRow>
    </thead>
  );
};

export default memo(PotentialPartnersFiltersContainer);
